import React, { useEffect, useState } from "react";
import { ymd } from "../lib/dates";
import { useNavigate } from "react-router-dom";
import { Money, CalendarBlank, IdentificationCard, CalendarDots, ArrowRight, Printer, Clock, TrendUp, User, CheckCircle } from "@phosphor-icons/react";
import api, { formatIDR, formatMonth, formatDate } from "../lib/api";

export default function KaryawanDashboard({ user }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [slips, setSlips] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [rapor, setRapor] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, s, l, r, e] = await Promise.all([
          api.get("/employees/me").catch(() => ({ data: null })),
          api.get("/slips/me").catch(() => ({ data: [] })),
          api.get("/leaves").catch(() => ({ data: [] })),
          api.get("/rapor").catch(() => ({ data: [] })),
          api.get("/calendar").catch(() => ({ data: [] })),
        ]);
        setProfile(p.data); setSlips(s.data); setLeaves(l.data); setRapor(r.data); setEvents(e.data);
      } catch (_) {}
    })();
  }, []);

  const latestSlip = slips[0];
  const latestRapor = rapor[0];
  const pendingLeaves = leaves.filter((l) => l.status === "pending").length;
  const approvedLeaves = leaves.filter((l) => l.status === "approved").length;
  const today = ymd();
  const upcomingEvents = events.filter((e) => e.start_date >= today).slice(0, 3);

  return (
    <div className="max-w-5xl mx-auto" data-testid="karyawan-dashboard">
      {/* Hero card - mobile-banking style */}
      <div className="rounded-2xl p-6 sm:p-8 text-white shadow-xl mb-6 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #48B8BD 0%, #0B8D95 100%)" }}>
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/5" />
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/80 font-semibold">Selamat Datang</div>
              <div className="font-display text-2xl sm:text-3xl font-bold mt-1">{user?.name}</div>
              <div className="text-sm text-white/80 mt-1">{profile ? `${profile.position} · ${profile.employee_id}` : "Karyawan Yayasan"}</div>
            </div>
            <div className="h-14 w-14 rounded-full bg-white/15 border border-white/30 flex items-center justify-center font-display text-2xl font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
          </div>

          {/* Latest slip summary card */}
          <div className="rounded-xl bg-white/15 backdrop-blur border border-white/20 p-4">
            <div className="text-xs text-white/80 uppercase tracking-wider mb-1">Take Home Pay Terbaru</div>
            {latestSlip ? (
              <>
                <div className="font-mono text-2xl sm:text-3xl font-bold">{formatIDR(latestSlip.net)}</div>
                <div className="text-xs text-white/80 mt-1">Periode: {formatMonth(latestSlip.period)}</div>
              </>
            ) : (
              <div className="text-sm text-white/85">Belum ada slip gaji</div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions - bank app style icons */}
      <div className="grid grid-cols-4 gap-3 mb-6" data-testid="karyawan-quick-actions">
        {[
          { to: "/slip-gaji", icon: Money, label: "Slip Gaji" },
          { to: "/cuti", icon: CalendarBlank, label: "Ajukan Cuti" },
          { to: "/rapor", icon: IdentificationCard, label: "Rapor" },
          { to: "/kalender", icon: CalendarDots, label: "Kalender" },
        ].map((m) => (
          <button key={m.to} onClick={() => navigate(m.to)} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white border border-gray-100 hover:border-[#48B8BD] hover:shadow-md transition-all group" data-testid={`karyawan-quick-${m.to.replace("/","")}`}>
            <div className="h-12 w-12 rounded-xl bg-[#F0FAFA] text-[#0B8D95] flex items-center justify-center group-hover:bg-[#48B8BD] group-hover:text-white transition-colors">
              <m.icon size={22} weight="duotone" />
            </div>
            <div className="text-xs font-semibold text-gray-700 text-center">{m.label}</div>
          </button>
        ))}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <div className="rounded-xl bg-white border border-gray-100 p-4">
          <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-2"><Clock size={14} /> Cuti Pending</div>
          <div className="font-display text-2xl font-bold text-gray-900">{pendingLeaves}</div>
        </div>
        <div className="rounded-xl bg-white border border-gray-100 p-4">
          <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-2"><CheckCircle size={14} /> Cuti Disetujui</div>
          <div className="font-display text-2xl font-bold text-[#0B8D95]">{approvedLeaves}</div>
        </div>
        <div className="rounded-xl bg-white border border-gray-100 p-4 sm:block col-span-2 sm:col-span-1">
          <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-2"><TrendUp size={14} /> Rapor Terakhir</div>
          {latestRapor ? (
            <div className="flex items-center gap-2">
              <span className="font-display text-2xl font-bold text-gray-900">{latestRapor.average}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-bold ${latestRapor.grade==="A"?"bg-[#CCFBF1] text-[#0B8D95]":"bg-blue-100 text-blue-700"}`}>{latestRapor.grade}</span>
              <span className="text-xs text-gray-500 ml-auto">{formatMonth(latestRapor.period)}</span>
            </div>
          ) : (
            <div className="text-sm text-gray-400">-</div>
          )}
        </div>
      </div>

      {/* Two-column: Recent slips + Upcoming events */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="font-display font-bold text-gray-900">Riwayat Slip Gaji</div>
            <button onClick={()=>navigate("/slip-gaji")} className="text-xs text-[#0B8D95] font-semibold flex items-center gap-1 hover:gap-1.5 transition-all">Lihat Semua <ArrowRight size={12} /></button>
          </div>
          <div className="divide-y divide-gray-100">
            {slips.slice(0, 4).map((s) => (
              <div key={s.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{formatMonth(s.period)}</div>
                  <div className="text-xs text-gray-500">Take Home: <span className="font-mono">{formatIDR(s.net)}</span></div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">Bruto</div>
                  <div className="text-sm font-mono font-semibold text-gray-900">{formatIDR(s.gross)}</div>
                </div>
              </div>
            ))}
            {slips.length === 0 && <div className="px-5 py-8 text-center text-sm text-gray-400">Belum ada slip gaji</div>}
          </div>
        </div>

        <div className="rounded-xl bg-white border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="font-display font-bold text-gray-900">Agenda Mendatang</div>
            <button onClick={()=>navigate("/kalender")} className="text-xs text-[#0B8D95] font-semibold flex items-center gap-1 hover:gap-1.5 transition-all">Lihat Kalender <ArrowRight size={12} /></button>
          </div>
          <div className="divide-y divide-gray-100">
            {upcomingEvents.map((e) => (
              <div key={e.id} className="px-5 py-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg flex flex-col items-center justify-center shrink-0" style={{ backgroundColor: (e.color || "#0B8D95") + "20", color: e.color || "#0B8D95" }}>
                  <div className="text-[10px] uppercase leading-none">{new Date(e.start_date).toLocaleDateString("id-ID", { month: "short" })}</div>
                  <div className="font-bold leading-none">{new Date(e.start_date).getDate()}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{e.title}</div>
                  <div className="text-xs text-gray-500 truncate">{e.location || "-"}</div>
                </div>
              </div>
            ))}
            {upcomingEvents.length === 0 && <div className="px-5 py-8 text-center text-sm text-gray-400">Tidak ada agenda mendatang</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
